import { useCallback, useState } from 'react'
import { addMonths, startOfMonth } from './upcomingTasks.utils'

interface VisibleRange {
  start: Date // first day of first rendered month
  end: Date // first day of last rendered month
}

interface UseVisibleMonthRangeArgs {
  /** Month to centre the initial window on (defaults to current month). */
  initialCenter?: Date
  /** Months to render on each side of the centre at mount. */
  initialPadding?: number
}

export function useVisibleMonthRange({
  initialCenter,
  initialPadding = 1,
}: UseVisibleMonthRangeArgs = {}): {
  range: VisibleRange
  extendUp: () => void
  extendDown: () => void
  /** Centre the range on `date`. Optional `padding` overrides the
   *  hook's default `initialPadding` — useful when the caller knows
   *  the target window will be sparse (e.g. months without tasks)
   *  and wants to seed enough rows to make the page scrollable
   *  without relying on the auto-fill cascade. */
  resetTo: (date: Date, padding?: number) => void
} {
  const [range, setRange] = useState<VisibleRange>(() => {
    const centre = startOfMonth(initialCenter ?? new Date())
    return {
      start: addMonths(centre, -initialPadding),
      end: addMonths(centre, initialPadding),
    }
  })

  const extendUp = useCallback(() => {
    setRange(prev => ({ start: addMonths(prev.start, -1), end: prev.end }))
  }, [])

  const extendDown = useCallback(() => {
    setRange(prev => ({ start: prev.start, end: addMonths(prev.end, 1) }))
  }, [])

  const resetTo = useCallback((date: Date, padding?: number) => {
    const p = padding ?? initialPadding
    const centre = startOfMonth(date)
    setRange({
      start: addMonths(centre, -p),
      end: addMonths(centre, p),
    })
  }, [initialPadding])

  return { range, extendUp, extendDown, resetTo }
}
